'use client';

import { useEffect, useRef, useSyncExternalStore } from 'react';
import { PinCard } from './PinCard';
import { PinSkeleton } from './PinSkeleton';
import { AdSlot } from './AdSlot';
import { useI18n } from '@/lib/i18n/context';

interface PostItem {
  id: string;
  src: string;
  thumb?: string;
  title: string;
  tag?: string;
  author?: { name: string; username?: string; avatar?: string };
  views?: number;
  likes?: number;
  isLiked?: boolean;
  isSaved?: boolean;
}

interface MasonryGridProps {
  posts: PostItem[];
  isLoading?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  skeletonCount?: number;
  isLastPage?: boolean;
}

/**
 * Số cột theo độ rộng màn hình (giữ đúng các breakpoint cũ của .masonry-grid:
 * <768px: 1 cột, 768–1279px: 2 cột, ≥1280px: 3 cột).
 */
const COLUMN_QUERIES: Array<[string, number]> = [
  ['(min-width: 1280px)', 3],
  ['(min-width: 768px)', 2],
];

function getColumnCount(): number {
  for (const [q, n] of COLUMN_QUERIES) {
    if (window.matchMedia(q).matches) return n;
  }
  return 1;
}

function subscribeColumnCount(cb: () => void) {
  const mqls = COLUMN_QUERIES.map(([q]) => window.matchMedia(q));
  mqls.forEach(m => m.addEventListener('change', cb));
  return () => mqls.forEach(m => m.removeEventListener('change', cb));
}

/** Chèn 1 native ad sau mỗi N pin */
const AD_EVERY_N = 10;

export function MasonryGrid({
  posts,
  isLoading = false,
  hasMore = false,
  onLoadMore,
  skeletonCount = 10,
  isLastPage = false,
}: MasonryGridProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  // Server render 1 cột (mobile-first), client cập nhật theo màn hình sau khi hydrate
  const columnCount = useSyncExternalStore(subscribeColumnCount, getColumnCount, () => 1);

  const hasMoreRef    = useRef(hasMore);
  const isLoadingRef  = useRef(isLoading);
  const onLoadMoreRef = useRef(onLoadMore);

  hasMoreRef.current    = hasMore;
  isLoadingRef.current  = isLoading;
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
          onLoadMoreRef.current?.();
        }
      },
      { rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  /**
   * Xây danh sách items: pin thông thường xen kẽ ad slot.
   * Native Ad được wrap trong masonry-item để hoà vào grid,
   * trông như 1 card bình thường — không làm vỡ layout.
   */
  const items: React.ReactNode[] = [];
  posts.forEach((post, i) => {
    items.push(<PinCard key={post.id} {...post} priority={i < 2} />);

    // if ((i + 1) % AD_EVERY_N === 0) {
    //   // Xen kẽ native provider để tăng fill rate
    //   const adType = 'native2';
    //   items.push(
    //     <div key={`ad-native-${i}`} className="masonry-item">
    //       <div className="rounded-2xl overflow-hidden bg-surface-800">
    //         <AdSlot type={adType} className="w-full" />
    //       </div>
    //       <p className="text-gray-600 text-[10px] text-center mt-1">Ads</p>
    //     </div>,
    //   );
    // }

    // Sau mỗi AD_EVERY_N pin (và không phải pin cuối), chèn native ad
    // if ((i + 1) % AD_EVERY_N === 0 && i < posts.length - 1) {
    //   // Xen kẽ native provider để tăng fill rate
    //   const adType = '728x90-2';
    //   items.push(
    //     <div key={`ad-native-${i}`} className="masonry-item">
    //       <div className="rounded-2xl overflow-hidden bg-surface-800">
    //         <AdSlot type={adType} className="w-full" />
    //       </div>
    //       <p className="text-gray-600 text-[10px] text-center mt-1">Ads</p>
    //     </div>,
    //   );
    // }
  });

  const allItems = isLoading
    ? [...items, ...Array.from({ length: skeletonCount }).map((_, i) => <PinSkeleton key={`sk-${i}`} />)]
    : items;

  return (
    <>
      {/*
        Chia item vào các cột theo vòng tròn (1→cột 1, 2→cột 2, 3→cột 3, 4→cột 1...)
        để thứ tự đọc là trái → phải theo hàng. CSS column-count cũ điền theo chiều dọc
        từng cột nên không làm được điều này.
      */}
      <div className="flex gap-4 items-start">
        {Array.from({ length: columnCount }).map((_, col) => (
          <div key={col} className="flex-1 min-w-0 flex flex-col">
            {allItems.filter((_, i) => i % columnCount === col)}
          </div>
        ))}
      </div>

      <div ref={sentinelRef} className="h-4" />

      {isLastPage && !hasMore && posts.length > 0 && !isLoading && (
        <>
          <p className="text-center text-gray-600 text-sm py-4 font-medium">
            ✦ {t.home.noMorePosts} ✦
          </p>
        </>
      )}
    </>
  );
}